/**
 * usePermission — jedno miejsce, w którym panel pyta „czy mi wolno" (F24 Kawałek 1).
 *
 * Do tej pory każdy ekran kopiował linijkę `isSuperUser || me.permissions.includes(kod)`, a
 * odpowiedź logowania zwracała pustą listę — więc bramki i tak nic nie robiły. Od F24 lista jest
 * realna, dlatego wzorzec musi mieszkać w jednym miejscu.
 *
 * **Dwa różne zachowania, świadomie rozdzielone** (decyzja właściciela):
 *   • czego NIE WIDZISZ — znika: pozycje menu, wejścia do ekranów, rekordy i typy na listach;
 *   • czego NIE MOŻESZ ZROBIĆ — zostaje widoczne, ale nieaktywne, z wyjaśnieniem po kliknięciu.
 * Pierwsze załatwia `<Can>`, drugie `usePermission(...).allowed` w parze z `<PermissionButton>`.
 * Chowanie przycisku „Edytuj" byłoby gorsze niż wyszarzenie: użytkownik nie wie, czy funkcji nie
 * ma, czy nie ma do niej prawa — a to pierwsze pytanie, które trafia na wsparcie.
 *
 * **Co dokładnie znaczy „wolno":** lista z odpowiedzi logowania niesie uprawnienia AKTYWNEJ FIRMY
 * na poziomie kartoteki/klasy. Zawężenia do konkretnego typu czy kategorii NIE są w niej
 * odzwierciedlone — o tym rozstrzyga serwer przy każdym żądaniu (pusta lista, 403 na szczególe).
 * Dlatego bramka w panelu jest podpowiedzią, nigdy kontrolą dostępu.
 *
 * Właściciel tenanta omija bramki — tak samo jak omija silnik po stronie serwera.
 */

import { useTenantAuth } from '@/contexts/TenantAuthContext';

export interface PermissionVerdict {
  allowed: boolean;
  /**
   * Dlaczego nie wolno — gotowy klucz tłumaczenia dla podpowiedzi przy nieaktywnym przycisku.
   * `null`, gdy wolno.
   */
  reasonKey: string | null;
}

const DENIED_REASON_KEY = 'tenant.permissions.denied.tooltip';

/** Czy użytkownik ma dany kod uprawnienia w aktywnej firmie. */
export function usePermission(code: string): PermissionVerdict {
  const { me } = useTenantAuth();

  if (me?.user.isSuperUser === true) return { allowed: true, reasonKey: null };

  const allowed = me?.permissions.includes(code) ?? false;
  return { allowed, reasonKey: allowed ? null : DENIED_REASON_KEY };
}

/**
 * Wariant dla kilku kodów naraz: „wolno, jeśli KTÓRYKOLWIEK".
 * Używany tam, gdzie jedna akcja ma dwa równoważne prawa (np. dekretacja faktur zakupu LUB
 * sprzedaży — te same ekrany obsługują oba kierunki).
 */
export function useAnyPermission(codes: readonly string[]): PermissionVerdict {
  const { me } = useTenantAuth();

  if (me?.user.isSuperUser === true) return { allowed: true, reasonKey: null };

  const allowed = codes.some((code) => me?.permissions.includes(code) ?? false);
  return { allowed, reasonKey: allowed ? null : DENIED_REASON_KEY };
}
